import React from 'react';

import { Box } from '@mui/material';

import { type Nullable } from '@hrlogics/helper-types';
import { type SxProp } from '@hrlogics/react-helper-types';
import { composeSx } from '@hrlogics/react-helper-utils';

import { ClearVerifyLogo } from 'src/components/common';

import { Avatar } from '../shared';

const AUTH_PAPER_BOX_VARIANT = {
  default: 'default',
  withCompanyLogo: 'withCompanyLogo',
} as const;

type AuthPaperBoxProps = {
  sx?: SxProp;
  variant?: keyof typeof AUTH_PAPER_BOX_VARIANT;
  companyLogoUrl?: Nullable<string>;
};

export const AuthPaperBox: React.FC<React.PropsWithChildren<AuthPaperBoxProps>> = ({
  sx,
  variant = 'default',
  companyLogoUrl,
  children,
}) => {
  const containerSxProp = React.useMemo((): SxProp => {
    const sxProp = sx || {};
    return composeSx(containerStyles, sxProp);
  }, [sx]);

  if (variant === AUTH_PAPER_BOX_VARIANT.withCompanyLogo) {
    return (
      <Box>
        <Box sx={companyLogoContainerStyles}>
          <Avatar src={companyLogoUrl} alt="Company Logo" sx={companyLogoStyles} />
        </Box>
        <Box sx={containerSxProp} margin="40px auto 0">
          {children}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={containerSxProp} margin="0 auto">
      <ClearVerifyLogo sx={platformLogoStyles} />
      {children}
    </Box>
  );
};

const MAX_WIDTH = 540;

const containerStyles: SxProp = {
  width: '100%',
  maxWidth: MAX_WIDTH,
  borderRadius: '8px',
  padding: 4,
  backgroundColor: '#fff',
  boxShadow:
    '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 1px 5px rgba(0, 0, 0, 0.12)',
};

const companyLogoContainerStyles: SxProp = {
  width: '100%',
  maxWidth: MAX_WIDTH,
  margin: '0 auto',
  textAlign: 'center',
};

const companyLogoStyles: SxProp = {
  width: 100,
  height: 100,
  margin: '0 auto',
};

const platformLogoStyles: SxProp = {
  display: 'flex',
  justifyContent: 'center',
  mb: 2,
};
